import { Input } from '@/components/ui/input';
import { apiClient } from '@borradh-workspace/api-client';
import {
  type PlaceDetailsResponse,
  type PlacePredictionResponse,
  type SearchPlacesResponse,
  placeDetailsResponseSchema,
  searchPlacesResponseSchema,
} from '@borradh-workspace/contracts';
import { Loader2, MapPin, Search } from 'lucide-react';
import {
  type Ref,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

export interface AddressResult {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  county: string;
  postalCode: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
  placeId?: string;
  /** Only a business pick may fill an empty location name. */
  name?: string;
}

export interface AddressAutocompleteHandle {
  /** A manual edit or another address source superseded this search. */
  cancelSelection: () => void;
}

interface AddressAutocompleteProps {
  ref?: Ref<AddressAutocompleteHandle>;
  id?: string;
  onPlaceSelect: (place: AddressResult) => void;
  /** Prevent saving the previous address while a new pick is loading. */
  onResolvingChange?: (pending: boolean) => void;
  defaultValue?: string;
  placeholder?: string;
  disabled?: boolean;
  onManualEntry?: () => void;
  /** Search businesses and addresses together rather than addresses only. */
  includeBusinesses?: boolean;
  /** Prefer this country while allowing an explicit overseas search. */
  country?: string;
}

const SEARCH_UNAVAILABLE =
  'Google Maps search is unavailable. Try again, or enter the address manually.';

function toAddressResult(place: PlaceDetailsResponse): AddressResult {
  return {
    addressLine1: place.addressLine1,
    addressLine2: place.addressLine2,
    city: place.city,
    county: place.county,
    postalCode: place.postalCode,
    country: place.country ?? '',
    latitude: place.latitude,
    longitude: place.longitude,
    placeId: place.placeId,
    ...(place.isBusiness && place.name ? { name: place.name } : {}),
  };
}

/**
 * Both web and native use our authenticated Places API, so search still works
 * when the deployment has a server Maps key but no public browser key. Keep
 * Google's ranking, show the business AND address, and only fill the form once
 * the owner picks a result and its structured address and pin are loaded.
 */
export function AddressAutocomplete({
  ref,
  id,
  onPlaceSelect,
  onResolvingChange,
  defaultValue = '',
  placeholder = 'Search for an address...',
  disabled = false,
  onManualEntry,
  includeBusinesses = false,
  country,
}: AddressAutocompleteProps) {
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const onPlaceSelectRef = useRef(onPlaceSelect);
  const onResolvingChangeRef = useRef(onResolvingChange);
  const sessionTokenRef = useRef<string | null>(null);
  const selectedValueRef = useRef<string | null>(defaultValue);
  const requestRef = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const [inputValue, setInputValue] = useState(defaultValue);
  const [predictions, setPredictions] = useState<PlacePredictionResponse[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  const cancelSelection = useCallback(() => {
    // Invalidate synchronously, before the parent's manual edit is rendered.
    // Even a response already queued as a microtask must not replace that edit.
    requestRef.current++;
    controllerRef.current?.abort();
    sessionTokenRef.current = null;
    selectedValueRef.current = '';
    setInputValue('');
    setPredictions([]);
    setActiveIndex(-1);
    setIsOpen(false);
    setIsSearching(false);
    setIsResolving(false);
    setError(null);
    onResolvingChangeRef.current?.(false);
  }, []);

  useImperativeHandle(ref, () => ({ cancelSelection }), [cancelSelection]);

  useEffect(() => {
    onPlaceSelectRef.current = onPlaceSelect;
    onResolvingChangeRef.current = onResolvingChange;
  });

  useEffect(() => {
    onResolvingChange?.(isResolving);
  }, [isResolving, onResolvingChange]);

  useEffect(() => {
    if (!disabled) return;
    requestRef.current++;
    controllerRef.current?.abort();
    setIsOpen(false);
    setIsResolving(false);
  }, [disabled]);

  useEffect(
    () => () => {
      requestRef.current++;
      controllerRef.current?.abort();
      onResolvingChangeRef.current?.(false);
    },
    []
  );

  useEffect(() => {
    if (
      !isOpen ||
      disabled ||
      inputValue.trim().length < 2 ||
      selectedValueRef.current === inputValue
    ) {
      setIsSearching(false);
      return;
    }

    const controller = new AbortController();
    controllerRef.current = controller;
    const request = ++requestRef.current;
    sessionTokenRef.current ??= crypto.randomUUID();
    const sessionToken = sessionTokenRef.current;
    setIsSearching(true);
    setError(null);
    setPredictions([]);
    setActiveIndex(-1);

    const timer = setTimeout(() => {
      void apiClient
        .get<SearchPlacesResponse>('places/autocomplete', {
          searchParams: {
            query: inputValue.trim(),
            types: includeBusinesses ? 'all' : 'addresses',
            sessionToken,
            ...(country ? { country } : {}),
          },
          signal: controller.signal,
          retry: 0,
          schema: searchPlacesResponseSchema,
        })
        .then((result) => {
          if (request !== requestRef.current || controller.signal.aborted)
            return;
          // The shared client reports schema mismatches by default. Search
          // results must be usable before they are offered as selectable rows.
          setPredictions(searchPlacesResponseSchema.parse(result).predictions);
        })
        .catch(() => {
          if (request !== requestRef.current || controller.signal.aborted)
            return;
          setError(SEARCH_UNAVAILABLE);
        })
        .finally(() => {
          if (request === requestRef.current && !controller.signal.aborted)
            setIsSearching(false);
        });
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [inputValue, includeBusinesses, country, isOpen, disabled]);

  const selectPlace = async (prediction: PlacePredictionResponse) => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const request = ++requestRef.current;
    const sessionToken = sessionTokenRef.current;
    // A details request ends the Google billing session; the next search gets
    // a new token, including when the details request fails.
    sessionTokenRef.current = null;
    setIsOpen(false);
    setIsSearching(false);
    setIsResolving(true);
    setError(null);
    try {
      const response = await apiClient.get<PlaceDetailsResponse>(
        'places/details',
        {
          searchParams: {
            placeId: prediction.placeId,
            ...(sessionToken ? { sessionToken } : {}),
          },
          signal: controller.signal,
          retry: 0,
          schema: placeDetailsResponseSchema,
        }
      );
      if (request !== requestRef.current || controller.signal.aborted) return;
      // Old API deployments return only a formatted address and coordinates.
      // Keep the current fields intact until the structured contract exists.
      const place = placeDetailsResponseSchema.parse(response);
      const label = place.formattedAddress || prediction.description;
      selectedValueRef.current = label;
      setInputValue(label);
      setPredictions([]);
      onPlaceSelectRef.current(toAddressResult(place));
    } catch {
      if (request === requestRef.current && !controller.signal.aborted) {
        setError(
          'We couldn’t load that address. Select it again, or enter the address manually.'
        );
      }
    } finally {
      if (request === requestRef.current && !controller.signal.aborted)
        setIsResolving(false);
    }
  };

  const showResults =
    !disabled &&
    isOpen &&
    inputValue.trim().length >= 2 &&
    selectedValueRef.current !== inputValue &&
    !error;

  return (
    <div className="relative space-y-1">
      <div className="relative">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          id={id}
          ref={inputRef}
          role="combobox"
          aria-label={
            id
              ? undefined
              : includeBusinesses
                ? 'Search for a business or address'
                : 'Search for an address'
          }
          aria-autocomplete="list"
          aria-expanded={showResults}
          aria-controls={showResults ? listId : undefined}
          aria-activedescendant={
            showResults && activeIndex >= 0
              ? `${listId}-${activeIndex}`
              : undefined
          }
          aria-busy={isSearching || isResolving}
          autoComplete="off"
          maxLength={200}
          className="pl-9 pr-9"
          placeholder={placeholder}
          value={inputValue}
          disabled={disabled}
          onFocus={() => setIsOpen(true)}
          onBlur={() => {
            setIsOpen(false);
            setActiveIndex(-1);
          }}
          onChange={(e) => {
            requestRef.current++;
            controllerRef.current?.abort();
            selectedValueRef.current = null;
            setInputValue(e.target.value);
            setPredictions([]);
            setActiveIndex(-1);
            setIsOpen(true);
            setIsResolving(false);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              if (showResults) e.stopPropagation();
              setIsOpen(false);
              return;
            }
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();
              setIsOpen(true);
              setActiveIndex((current) =>
                predictions.length === 0
                  ? -1
                  : e.key === 'ArrowDown'
                    ? (current + 1) % predictions.length
                    : current <= 0
                      ? predictions.length - 1
                      : current - 1
              );
            }
            if (e.key === 'Enter' && (showResults || isResolving)) {
              e.preventDefault();
              if (showResults && activeIndex >= 0 && predictions[activeIndex]) {
                void selectPlace(predictions[activeIndex]);
              }
            }
          }}
        />
        {(isSearching || isResolving) && (
          <Loader2
            aria-hidden="true"
            className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin text-muted-foreground"
          />
        )}
      </div>
      {showResults && (
        <div className="absolute z-50 w-full overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-lg">
          <div
            id={listId}
            role="listbox"
            tabIndex={-1}
            aria-label="Google Maps results"
            className="max-h-[300px] space-y-0.5 overflow-y-auto p-1.5"
          >
            {predictions.map((prediction, index) => (
              <button
                id={`${listId}-${index}`}
                key={prediction.placeId}
                role="option"
                aria-selected={activeIndex === index}
                type="button"
                tabIndex={-1}
                className="group flex w-full items-start gap-3 rounded-md px-3 py-2.5 text-left text-sm transition-colors hover:bg-accent aria-selected:bg-accent aria-selected:text-accent-foreground"
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => {
                  void selectPlace(prediction);
                }}
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted/60 text-muted-foreground group-aria-selected:bg-background group-aria-selected:text-foreground">
                  <MapPin aria-hidden="true" className="size-4" />
                </span>
                <span className="min-w-0 flex-1 space-y-0.5">
                  <span className="block break-words font-medium leading-5">
                    {prediction.mainText}
                  </span>
                  {prediction.secondaryText && (
                    <span className="block break-words text-xs leading-5 text-muted-foreground">
                      {prediction.secondaryText}
                    </span>
                  )}
                </span>
              </button>
            ))}
            {isSearching && (
              <p
                role="status"
                className="px-3 py-3 text-sm text-muted-foreground"
              >
                Searching Google Maps…
              </p>
            )}
            {!isSearching && !error && predictions.length === 0 && (
              <p
                role="status"
                className="px-3 py-3 text-sm text-muted-foreground"
              >
                No places found. Try the business name with its town, or enter
                the address manually.
              </p>
            )}
          </div>
          {/* Unmodified Google product icon:
              https://www.gstatic.com/images/branding/product/2x/maps_48dp.png */}
          <div className="flex min-h-12 items-center justify-center gap-3 border-t border-border/60 bg-muted/30 px-4 py-3">
            <span className="text-xs font-medium text-muted-foreground">
              Powered by
            </span>
            <span className="flex items-center gap-2.5">
              <img
                src="/google-maps-pin.png"
                alt="Google Maps"
                width={24}
                height={24}
                className="size-6 shrink-0"
              />
              <span
                aria-hidden="true"
                translate="no"
                className="whitespace-nowrap font-sans text-sm font-normal not-italic tracking-normal text-[#1F1F1F] dark:text-white"
              >
                Google Maps
              </span>
            </span>
          </div>
        </div>
      )}
      {isResolving && (
        <p role="status" className="text-xs text-muted-foreground">
          Loading address…
        </p>
      )}
      {error && (
        <p role="alert" className="text-xs text-muted-foreground">
          {error}
        </p>
      )}
      {onManualEntry && (
        <button
          type="button"
          className="text-xs text-muted-foreground underline"
          onClick={() => {
            cancelSelection();
            onManualEntry();
          }}
        >
          Enter address manually
        </button>
      )}
    </div>
  );
}
